import { ISimpleListResponse, useXHR, XHRInstance } from "@sembark-travel/xhr"
import { IAddress } from "../Addresses"
import { ICabSchedule, IScheduledCab } from "../CabScheduling"
import { IBooking } from "../HotelBookings"
import { ILocation } from "../Locations"
import { TTouristProfile } from "../TouristProfiles"
import { TTravelActiviytBooking } from "../TravelActivityBookings"
import { ILatestGivenQuote, ITrip } from "../Trips"
import { ITripSource } from "../TripSources"
import { IPhoneNumber, IEmail } from "./../Contacts"
import { TTripDestination } from "./../TripDestinations"
import useSWR from "swr"
import { showSnackbar } from "@sembark-travel/ui-snackbar"

export type TTouristDocumentType = {
	id: number
	name: string
	deleted_at?: string
	destinations?: {
		id: number
		name: string
		short_name: string
	}[]
}

export type TTouristDocument = {
	id: number | null
	document_type: TTouristDocumentType | null
	uid: string | null
	description?: string
	file?: File
	media_url?: string
	removed_file?: string
}

export type TTourist = {
	id: number
	trip_id: number
	name: string
	name_without_salutation: string
	is_primary: 0 | 1 | "0" | "1"
	phone_number: string
	email?: string
	phone_numbers: Array<IPhoneNumber<TTourist>>
	emails?: Array<IEmail<TTourist>>
	destinations?: Array<TTripDestination>
	latest_given_quote?: ILatestGivenQuote
	latest_stage_name?: string
	trip_source?: ITripSource
	address?: IAddress
	trip?: ITrip
	documents: Array<TTouristDocument>
	dob?: string
	age_during_trip?: number
	nationality?: ILocation["country"]
	type: "adult" | "child"
	salutation?: "Mr." | "Mrs." | "Ms."
	gender?: "male" | "female" | "others"
	remarks?: string
	comments?: string
	booking_tourist?: TBookingTourists
	booking_tourists?: TBookingTourists[]
	groups?: TTouristMembershipGroup[]
	profile_id?: number
	profile?: TTouristProfile
	created_at: string
	deleted_at?: string
	document_tracking?: TDocumentTracking
}

type TDocumentTracking = {
	completed_count: number
	pending_count: number
	total_required: number
	media_url?: string
	is_document_complete: boolean
	document_list?: {
		name: string
		document_id?: number
		document_type_id: number
		media_url?: string
		is_uploaded?: boolean
		file?: File
	}[]
	pending_list?: {
		id: number
		name: string
	}[]
}

type TTouristMembershipGroup = {
	id: number
	name: string
	trip_id: number
}

export type TBookingType =
	| "hotel_bookings"
	| "cab_schedules"
	| "scheduled_cabs"
	| "travel_activity_bookings"

export type TBookingTourists<
	TBookingType =
		| IBooking
		| IScheduledCab
		| ICabSchedule
		| TTravelActiviytBooking,
> = {
	booking: TBookingType
	booking_id: number
	booking_type: string
	is_primary: number
	remarks: string
	id: number
}

export type TTouristGroup<
	TBookingType =
		| IBooking
		| IScheduledCab
		| ICabSchedule
		| TTravelActiviytBooking,
> = {
	id: number
	trip_id: number
	name: string
	members: Array<{
		created_at: string
		updated_at: string
		is_primary: 0 | 1 | "0" | "1" | boolean
		tourist: TTourist
	}>
	booking_tourist_members?: Array<TBookingTourists<TBookingType>>
}

export interface IGroupedBookingsResponse {
	hotel_bookings: Array<TBookingTourists<IBooking>>
	cab_schedules: Array<TBookingTourists<ICabSchedule>>
	travel_activity_bookings: Array<TBookingTourists<TTravelActiviytBooking>>
}

export type TChildrenArray = Array<{ count: number; age: number }>

export type TTouristMember<TMemberType = TTourist | TTouristGroup> = {
	member: TMemberType
	booking_id: number
	booking_type:
		| "cab_schedules"
		| "scheduled_cabs"
		| "hotel_bookings"
		| "travel_activity_bookings"
	member_id: number
	member_type: "tourists" | "tourist_groups"
	has_cab_time_variation?: boolean
}

export type TRemoveDocumentPayload = {
	documentId?: number | null
	touristId?: number | null
	url?: string
}

export function TouristXHR(xhr: XHRInstance) {
	return {
		async getAllTourist({
			tripId,
			includes,
		}: {
			tripId: number
			includes: string[]
		}) {
			return xhr
				.get<ISimpleListResponse<TTourist>>(`/tourists`, {
					params: {
						trip: tripId,
						include: includes.join(","),
						pagination: "page",
						limit: 1000,
					},
				})
				.then((res) => res.data.data)
		},
		async getDocumentByTouristId({ touristId }: { touristId: number }) {
			return xhr
				.get<{ data: TTourist }>(`/tourists/${touristId}`)
				.then((res) => res.data.data)
		},
		async preUploadDocument(file: File): Promise<{
			media_url: string
			message: string
		}> {
			const data = new FormData()
			if (file) {
				data.set("file", file)
			}
			return xhr
				.post("/temporary-media", data, {
					headers: {
						"Content-Type": "multipart/form-data",
					},
				})
				.then((res) => {
					showSnackbar(res.data?.message)
					return res.data
				})
		},
		async uploadDocumentByDocumentId(
			touristId: number,
			documentId: number,
			file: File
		): Promise<{
			media_url: string
			message: string
		}> {
			const data = new FormData()
			if (file) {
				data.set("file", file)
			}
			return xhr
				.post(`tourists/${touristId}/documents/${documentId}/file`, data, {
					headers: {
						"Content-Type": "multipart/form-data",
					},
				})
				.then((res) => res.data)
				.then((res) => {
					showSnackbar(res.message)
					return res
				})
		},
		async deleteTouristDocument({
			url,
			touristId,
			documentId,
		}: TRemoveDocumentPayload) {
			return xhr
				.delete("/tourists/documents/file", {
					params: {
						...(url ? { media_url: url } : {}),
						...(touristId ? { tourist_id: touristId } : {}),
						...(documentId ? { document_id: documentId } : {}),
					},
				})
				.then((res) => res.data)
				.catch((error) => {
					showSnackbar(
						error?.message || "Failed to delete document. Please try again."
					)
				})
		},
	}
}
export function useTourist({
	tripId,
	includes = ["bookings", "groups", "address", "profile"],
}: {
	tripId: number
	includes?: string[]
}) {
	const xhr = useXHR()
	const {
		data: tourists,
		isLoading,
		isValidating,
		error,
		mutate,
	} = useSWR(
		"/tourists?trip=" +
			tripId +
			(includes.length > 0 ? "&include=" + includes.join(",") : ""),
		() => TouristXHR(xhr).getAllTourist({ tripId, includes })
	)
	const isFetching = !tourists && !error
	return {
		tourists,
		error,
		mutate,
		isFetching,
		isLoading,
		isValidating,
	}
}

export function useDocuments({
	tripId,
	tripDocumentsOnly,
}: {
	tripId: number
	tripDocumentsOnly: boolean
}) {
	const xhr = useXHR()
	const {
		data: documents,
		isLoading,
		isValidating,
		error,
		mutate,
	} = useSWR(`/document-types?trip_id=${tripId}`, () =>
		xhr
			.get<ISimpleListResponse<TTouristDocumentType>>("/document-types", {
				params: { trip_id: tripId, trip_documents_only: tripDocumentsOnly },
			})
			.then((res) => res.data.data)
	)
	const isFetching = !documents && !error
	return {
		documents,
		error,
		mutate,
		isFetching,
		isLoading,
		isValidating,
	}
}

export async function deleteDocument({
	documentId,
	url,
	touristId,
	xhr,
}: TRemoveDocumentPayload & { xhr: XHRInstance }) {
	return await TouristXHR(xhr).deleteTouristDocument({
		documentId,
		url,
		touristId,
	})
}
